import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Users } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { useSettings } from "@/context/SettingsContext";
import { Skeleton } from "@/components/ui/skeleton";

export const TeamSection = () => {
  const [team, setTeam] = useState(null);
  const { play } = usePlayer();
  const { homepage } = useSettings();

  useEffect(() => {
    api.get("/team/random").then((r) => setTeam(r.data)).catch(() => setTeam([]));
  }, []);

  const [loaded, setLoaded] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const loadedOne = () => setLoaded((n) => n + 1);
  useEffect(() => {
    if (!team) return;
    const t = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [team]);
  const withImages = team ? team.filter((m) => m.image_url).length : 0;
  const ready = !!team && (loaded >= withImages || timedOut);

  return (
    <section id="team" className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-14 pb-14" data-testid="team-section">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          {homepage.team_eyebrow && <p className="eyebrow mb-2">{homepage.team_eyebrow}</p>}
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{homepage.team_title || "Our Team"}</h2>
          {homepage.team_subtitle && <p className="mt-2 text-sm text-slate-400">{homepage.team_subtitle}</p>}
        </div>
        <Link to="/hosts" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white whitespace-nowrap" data-testid="team-roster-link"><Users size={18} className="text-sky-400" /> Full roster</Link>
      </div>
      {team && team.length === 0 ? (
        <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="team-empty">Add podcasters in the admin dashboard.</div>
      ) : (
        <>
        {!ready && <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="team-skeleton">{[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl bg-white/5" />)}</div>}
        {team && <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${ready ? "" : "invisible h-0 overflow-hidden"}`} data-testid="team-grid">
          {team.map((m, i) => (
            <article key={m.id} className={`on-dark group relative aspect-[3/4] rounded-2xl overflow-hidden card-surface ${ready ? "reveal" : ""}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`team-member-card-${i}`}>
              <Link to={`/hosts/${m.slug}`} className="absolute inset-0" aria-label={`View ${m.name}'s profile`} data-testid={`team-member-link-${i}`}>
                {m.image_url ? <img src={imgUrl(m.image_url)} alt={m.name} onLoad={loadedOne} onError={loadedOne} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="eager" /> : <div className="absolute inset-0 bg-[#053671]" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070F1E] via-[#070F1E]/60 to-transparent" />
              </Link>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <p className="text-[11px] font-mono uppercase tracking-widest text-sky-400">{m.role}</p>
                <Link to={`/hosts/${m.slug}`} className="block font-display font-bold text-lg leading-tight hover:text-sky-300 transition-colors" data-testid={`team-member-name-${i}`}>{m.name}</Link>
                {m.show && <Link to={`/shows/${m.show.slug}`} className="text-xs text-slate-300 hover:text-white">{m.show.name}</Link>}
                {m.latest_episode && (
                  <button onClick={() => play(m.latest_episode)} className="mt-3 w-full flex items-center gap-2 rounded-lg bg-white/10 hover:bg-sky-400 hover:text-[#070F1E] px-2.5 py-2 text-left transition-colors" data-testid={`team-member-latest-${i}`}>
                    <Play size={12} fill="currentColor" className="shrink-0" />
                    <span className="text-xs font-semibold truncate">{m.latest_episode.title}</span>
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>}
        </>
      )}
    </section>
  );
};
